import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, CalendarClock, MapPin, Link2, Unlink, Loader2, History, Play } from "lucide-react";
import api, { getErrorMsg } from "../../lib/api";
import { Button } from "../ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => (d && d !== "0000-00-00" ? d.slice(0, 10).split("-").reverse().join(".") : "–");
const dt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const dauer = (s) => (s == null ? null : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

const PHASE = {
  active: ["bg-emerald-100 text-emerald-700", "läuft"],
  upcoming: ["bg-amber-100 text-amber-700", "kommend"],
  past: ["bg-gray-100 text-gray-500", "beendet"],
  unknown: ["bg-gray-100 text-gray-500", "ohne Zeitraum"],
};

export function EventZeile({ ev, library, offen, onToggle, onChange }) {
  const [ziel, setZiel] = useState("");
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("token");
  const [cls, label] = PHASE[ev.phase] || PHASE.unknown;
  const verknuepft = new Set(ev.files.map(f => f.library_id).filter(Boolean));
  const waehlbar = library.filter(f => (f.status === "ready" || f.status === "ready_unprocessed") && !verknuepft.has(f.id));
  const gespielt = ev.files.reduce((n, f) => n + (f.plays || 0), 0);

  const verknuepfen = async () => {
    if (!ziel) return;
    setBusy(true);
    try { await api.post(`/mbox/library/${ziel}/to-order/${ev.order_pk}`); toast.success("Verknüpft – wird auf die Masten übertragen"); setZiel(""); onChange(); }
    catch (e) { toast.error(getErrorMsg(e, "Verknüpfen fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  const loesen = async (f) => {
    if (!window.confirm(`„${f.name}“ von „${ev.event || `Auftrag ${ev.order_pk}`}“ lösen?\n\nDie Durchsage wird von den Masten dieser Veranstaltung entfernt (inkl. Timer). In Ihrer Durchsagen-Datenbank bleibt sie erhalten.`)) return;
    try { await api.delete(`/mbox/orders/${ev.order_pk}/audio/${f.id}`); toast.success("Verknüpfung gelöst"); onChange(); }
    catch (e) { toast.error(getErrorMsg(e, "Lösen fehlgeschlagen")); }
  };

  return (
    <li className="border-b border-gray-100 last:border-0" data-testid={`event-audio-${ev.order_pk}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-2 py-3 text-left hover:bg-gray-50 rounded-md px-1" data-testid={`event-audio-toggle-${ev.order_pk}`}>
        {offen ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-semibold truncate ${ev.phase === "past" ? "text-gray-600" : "text-gray-900"}`}>{ev.event || `Auftrag ${ev.order_pk}`}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls}`}>{label}</span>
          </span>
          <span className="block text-[11px] text-gray-400">{fmt(ev.dispo_start)} – {fmt(ev.dispo_end)}</span>
        </span>
        <span className="text-[11px] text-gray-500 shrink-0 text-right">
          {ev.files.length} Durchsage{ev.files.length === 1 ? "" : "n"}
          {gespielt > 0 && <span className="block text-gray-400">{gespielt}× gespielt</span>}
        </span>
      </button>

      {offen && (
        <div className="pl-6 pr-1 pb-4 space-y-3" data-testid={`event-audio-detail-${ev.order_pk}`}>
          {ev.address && <p className="text-[11px] text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /> {ev.address}</p>}
          {ev.phase === "past" && (
            <p className="text-[11px] text-gray-500 flex items-center gap-1"><History className="w-3 h-3" /> Veranstaltung beendet – Übersicht, welche Durchsagen genutzt wurden.</p>
          )}
          {ev.files.length === 0 ? (
            <p className="text-xs text-gray-400">Noch keine Durchsage verknüpft.</p>
          ) : (
            <ul className="space-y-2">
              {ev.files.map(f => (
                <li key={f.id} className="bg-gray-50 border border-gray-100 rounded-lg p-2.5" data-testid={`event-audio-file-${f.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{f.name}</span>
                    {dauer(f.duration_sec) && <span className="text-[11px] text-gray-400">{dauer(f.duration_sec)} min</span>}
                    <span className="text-[11px] text-gray-500 flex items-center gap-1 sm:ml-auto">
                      <Play className="w-3 h-3" /> {f.plays ? `${f.plays}× gespielt · zuletzt ${dt(f.last_played)}${f.last_played_by ? ` (${f.last_played_by})` : ""}` : "nicht gespielt"}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {(f.status === "ready" || f.status === "ready_unprocessed") ? (
                      <audio controls preload="none" className="h-8 w-full sm:max-w-sm" src={`${API_BASE}/api/mbox/orders/${ev.order_pk}/audio/${f.id}/stream?token=${token}`} data-testid={`event-audio-preview-${f.id}`} />
                    ) : <span className="text-[11px] text-amber-600">wird verarbeitet…</span>}
                    {ev.editable && ev.phase !== "past" && (
                      <Button size="sm" variant="ghost" onClick={() => loesen(f)} className="text-gray-400 hover:text-red-600 h-10 sm:h-8 shrink-0" title="Verknüpfung lösen" data-testid={`event-audio-unlink-${f.id}`}>
                        <Unlink className="w-4 h-4" /><span className="ml-1 hidden sm:inline">Lösen</span>
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {ev.editable && ev.phase !== "past" && (
            <div className="flex items-center gap-2">
              <select value={ziel} onChange={e => setZiel(e.target.value)} className="h-10 sm:h-9 flex-1 min-w-0 px-2 border border-gray-200 rounded-md text-base sm:text-sm bg-white" data-testid={`event-audio-link-select-${ev.order_pk}`}>
                <option value="">{waehlbar.length ? "Durchsage aus der Datenbank verknüpfen…" : "Keine weitere Durchsage in der Datenbank"}</option>
                {waehlbar.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <Button size="sm" onClick={verknuepfen} disabled={!ziel || busy} className="bg-fuchsia-600 hover:bg-fuchsia-700 h-10 sm:h-9 px-3" data-testid={`event-audio-link-btn-${ev.order_pk}`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}<span className="ml-1 hidden sm:inline">Verknüpfen</span>
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// "Durchsagen verwalten": Veranstaltungen kompakt, Klick klappt auf (Vorhoeren + Verknuepfen, vergangene nur lesend)
export function CustomerEventAudioList({ rev = 0, onChange }) {
  const [events, setEvents] = useState(null);
  const [library, setLibrary] = useState([]);
  const [offen, setOffen] = useState(null);
  const [zeigeAlte, setZeigeAlte] = useState(false);

  const load = useCallback(() => {
    api.get("/mbox/library/events", { params: { all_past: true } }).then(r => setEvents(r.data.events || [])).catch(() => setEvents([]));
    api.get("/mbox/library").then(r => setLibrary(r.data.files || [])).catch(() => setLibrary([]));
  }, []);
  useEffect(() => { load(); }, [load, rev]);

  const aktuell = (events || []).filter(e => e.phase !== "past");
  const alte = (events || []).filter(e => e.phase === "past");
  const geaendert = () => { load(); onChange?.(); };
  const zeile = (ev) => (
    <EventZeile key={ev.order_pk} ev={ev} library={library} offen={offen === ev.order_pk}
      onToggle={() => setOffen(o => (o === ev.order_pk ? null : ev.order_pk))} onChange={geaendert} />
  );

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 sm:p-5" data-testid="customer-event-audio">
      <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-fuchsia-600" /> Durchsagen je Veranstaltung</h2>
      <p className="text-xs text-gray-400 mt-0.5 mb-2">Veranstaltung anklicken: verknüpfte Durchsagen vorhören, weitere aus der Datenbank verknüpfen oder lösen.</p>
      {events === null ? (
        <p className="text-sm text-gray-400">Lade Veranstaltungen…</p>
      ) : (
        <>
          {aktuell.length === 0 ? (
            <p className="text-sm text-gray-400 py-2" data-testid="event-audio-empty">Keine laufende oder kommende Veranstaltung.</p>
          ) : <ul>{aktuell.map(zeile)}</ul>}
          {alte.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2">
              <button type="button" onClick={() => setZeigeAlte(v => !v)} className="text-xs text-gray-500 hover:text-fuchsia-700 flex items-center gap-1" data-testid="event-audio-past-toggle">
                {zeigeAlte ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <History className="w-3.5 h-3.5" /> Vergangene Veranstaltungen ({alte.length})
              </button>
              {zeigeAlte && <ul className="mt-1">{alte.map(zeile)}</ul>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
